import {
  AgentLanguageInput,
  NodeDefinition,
  SkillConfig,
  ValidationResponse,
  VariableConnector,
  VariableTypeSizeConnector,
} from "@/generated";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { NoxusFormEntryConfig } from "../ConfigDrawer";
import { evaluateVisibilityRule } from "@/components/Form/configVisibility";
import { useMemo, useCallback, useState, useContext } from "react";
import { DataTypeIcon, hasTypeIcon } from "@/icons/DataTypeIcon";
import { GoogleDrivePickerField } from "@/components/Form/GoogleDrivePickerField";
import { IntegrationPickerField } from "@/components/Form/IntegrationPickerField";
import { CredentialPickerField } from "@/components/Form/CredentialPickerField";

import { DynamicSelectField } from "@/components/Form/DynamicSelectField";
import { OptionType, SpotSelectField } from "@/components/Form/SelectField";
import { WORKFLOW_ICONS } from "@/utils/consts";
import { NumberInputField } from "@/components/Form/NumberInputField";
import { ToggleField } from "@/components/Form/ToggleField";
import { ToggleGroupField } from "@/components/Form/ToggleGroupField";
import { CopyableUrlField } from "@/components/Form/CopyableUrlField";
import { ModelSelectField } from "@/components/Form/ModelSelectField";
import { ModelToggleField } from "@/components/Form/ModelToggleField";
import { CodeField } from "@/components/Form/CodeField";
import { CodeFieldAI } from "@/components/Form/CodeFieldAI";
import {
  OptionType as HorizontalOptionType,
  HorizontalSelectField,
  iconMap,
} from "@/components/Form/HorizontalSelectField";
import { EnumSliderField } from "@/components/Form/EnumSliderField";
import { GuardrailCard } from "@/components/Assistant/GuardrailCard";
import { IODefinition, NoxusReactflowNode } from "@/types/spotflow";
import { NoxusInputField } from "@/components/Form/InputField";
import { WorkspaceSecretRefField } from "@/components/Variables/WorkspaceSecretRefField";
import { JsonSchemaBuilderConfig } from "./JsonSchemaBuilderConfig";
import { ExtractionSchemaBuilderConfig } from "./ExtractionSchemaBuilderConfig";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import { ConfigFormRendererContext } from "@/components/Form/ConfigFormRenderer";
import { BindableTextField } from "@/components/SpotflowV2/Sidebar/BindableTextField";
import { BindableBigTextField } from "@/components/SpotflowV2/Sidebar/BindableBigTextField";
import { BindableCodeOrBigTextField } from "@/components/SpotflowV2/Sidebar/BindableCodeOrBigTextField";
import { DictSelectArrayWithBoundShape } from "@/components/SpotflowV2/Sidebar/DictSelectArrayWithBoundShape";
import { BindableDictRowsField } from "@/components/SpotflowV2/Sidebar/BindableDictRowsField";
import { BindableDictSelectRowsField } from "@/components/SpotflowV2/Sidebar/BindableDictSelectRowsField";
import { BindableTypedDictRowsField } from "@/components/SpotflowV2/Sidebar/BindableTypedDictRowsField";
import { BindableFileField } from "@/components/SpotflowV2/Sidebar/BindableFileField";
import { BindableNumberField } from "@/components/SpotflowV2/Sidebar/BindableNumberField";
import { BindableDateTimeField } from "@/components/SpotflowV2/Sidebar/BindableDateTimeField";
import { BindableBoolField } from "@/components/SpotflowV2/Sidebar/BindableBoolField";
import { BindableSelectField } from "@/components/SpotflowV2/Sidebar/BindableSelectField";
import { BindableChipsSelectField } from "@/components/SpotflowV2/Sidebar/BindableChipsSelectField";
import { StructuredFieldsField } from "@/components/SpotflowV2/Sidebar/StructuredFieldsField";
import { PathValuesField } from "@/components/SpotflowV2/Sidebar/PathValuesField";
import { TableFilterRowsField } from "@/components/SpotflowV2/Sidebar/TableFilterRowsField";
import { BindableConditionField } from "@/components/SpotflowV2/Sidebar/BindableConditionField";
import { BindableRoutesField } from "@/components/SpotflowV2/Sidebar/BindableRoutesField";
import { SwitchBranchesField } from "@/components/SpotflowV2/Sidebar/SwitchBranchesField";
import { BindableMergePathsField } from "@/components/SpotflowV2/Sidebar/BindableMergePathsField";
import {
  ConfigBigText,
  ConfigChipsSelect,
  ConfigMenuSelect,
  ConfigGdrivePicker,
  ConfigModelSelect,
  ConfigNumberRange,
  ConfigNumber,
  ConfigSelect,
  ConfigText,
  ConfigPassword,
  ConfigTextCodeAI,
  APIKeyField,
  ConfigToggle,
  ConfigToggleButton,
  ConfigTypeSelect,
  ConfigVariableInputValue,
  VisibleIf,
  VisibleIfNot,
  DisabledIf,
  ConfigTypeDictArray,
  ConfigDynamicSelect,
  ConfigDynamicText,
  ConfigTimePicker,
  ConfigMultiSelect,
  ConfigChipsText,
  ConfigChipsListText,
  ConfigRichTextVariables,
  ConfigDictListWithoutConnector,
  ConfigCriteriaList,
  ConfigDictComplexList,
  ConfigOneDrivePicker,
  ConfigBanner,
  ConfigBindableBigText,
  ConfigBindableBool,
  ConfigBindableCode,
  ConfigBindableCondition,
  ConfigBindableDictRows,
  ConfigBindableDictSelectRows,
  ConfigBindableTypedDictRows,
  ConfigBindableFile,
  ConfigBindableNumber,
  ConfigBindableDateTime,
  ConfigBindableSelect,
  ConfigBindableChipsSelect,
  ConfigStructuredFields,
  ConfigTableFilterRows,
  ConfigBindableRoutes,
  ConfigBindableSwitch,
  ConfigPathValues,
  ConfigBindableMergePaths,
  ConfigBindableText,
  ConfigNumberSlider,
  ConfigNumberRangeSlider,
  ConfigTextDisplay,
  ConfigRichTextVariablesAI,
  ConfigComplexCondition,
  ConfigScrapeWebsiteList,
  ConfigDictComplexListStandalone,
  ConfigOutputSelector,
  ConfigUserSelector,
  ConfigColorSelect,
  ConfigToolsSelect,
  ConfigIntroLanguagesWarning,
  ConfigLanguagesList,
  ConfigMCPCredentialSelect,
  ConfigMCPCredentialPicker,
  ConfigMCPToolPicker,
  ConfigEnumSlider,
  ConfigGuardrailCard,
  ConfigIconPicker,
  ConfigFileBase64,
  ConfigFileArray,
  ConfigImageUpload,
  ConfigWorkflowVersion,
  ConfigProviderApiToggle,
  ConfigCoworkerSelect,
  ConfigModelToggle,
  ConfigDictSelectArray,
  ConfigInputVariableSelector,
  ConfigDictTextAndSelectArray,
  ConfigUserTableSelector,
  VisibleIfGroup,
  ColorThemeSelectField as ConfigColorThemeSelectField,
  ConfigIntegrationPicker,
  ConfigCredentialPicker,
  ConfigDivider,
  ConfigFieldBadge,
  ConfigAssistantVersion,
  ConfigJsonSchemaBuilder,
  ConfigDictEntryWithType,
  ConfigTipTapList,
  ConfigExtractionSchema,
  ConfigCardSelect,
  ConfigWorkflowPicker,
  ConfigArrayField,
  ConfigCopyableUrl,
} from "@/types/api/spotflow/spotflow-config";
import { KBPickerField } from "./KBPickerField";
import { ToolSelectField } from "./ToolSelectField";
import { FieldBadgeField } from "@/components/Form/FieldBadgeField";
import { FileUploadField } from "@/components/Form/FileInputField";
import { DatePickerField } from "@/components/Form/DatePickerField";
import { FileBase64Field } from "@/components/Form/FileBase64Field";
import { ITypeDictValue, TypeDictField } from "@/components/Form/TypeDictField";
import { SlackChannelField } from "@/components/Form/SlackChannelField";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import MultiSelectField from "@/components/Form/MultiSelectField";
import {
  ChipInputField,
  ChipInputListField,
} from "@/components/Form/ChipInputField";
import { RichTextField } from "@/components/Form/RichTextField";
import {
  ComplexMarkdownField,
  AIComplexMarkdownField,
  useEditorInputs,
} from "@/components/Form/ComplexMarkdownField";
import { DictListField } from "@/components/Form/DictListField";
import { CriteriaListField } from "@/components/Form/CriteriaListField";
import { ComplexDictConfig } from "./ComplextDictConfig";
import { OneDrivePickerField } from "@/components/Form/OneDrivePickerField";
import { NumberSliderField } from "@/components/Form/NumberSliderField";
import { NumberRangeSliderField } from "@/components/Form/NumberRangeSliderField";
import { Banner } from "@/components/Banner";
import { RichTextFieldAI } from "@/components/Form/RichTextFieldAI";
import { ComplexConditionalField } from "@/components/Form/ComplexConditionalField";
import { WebsiteScrapeField } from "@/components/Form/WebsiteScrapeField";
import ErrorBoundary from "@/components/ErrorBoundary";
import { triggerPylonMessage } from "@/utils/pylon";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { OutputSelectorField } from "@/components/Form/OutputSelectorField";
import { UserSelectField } from "@/components/Form/UserSelectField";
import { ConfigToolsField } from "@/components/Assistant/ConfigToolsField";
import { LanguagesField } from "@/components/Assistant/LanguagesField";
import { IntroLanguagesWarning } from "@/components/AppManagementV2/IntroLanguagesWarning";
import { MCPCredentialField } from "@/components/MCP/MCPCredentialField";
import {
  MCPCredentialPickerField,
  MCPToolPickerField,
} from "@/components/MCP/MCPCredentialPickerField";
import { SkillsFieldWithHeader } from "@/components/Assistant/Skills/SkillsFieldWithHeader";
import {
  GuidelinesFieldWithHeader,
  GlossaryFieldStandalone,
} from "@/components/Assistant/Guidelines/GuidelinesFields";
import { ToolSectionHeader } from "@/components/Assistant/ToolSectionHeader";
import { Braces, KeyRound } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import { NoxusMenu } from "@/components/UI/NoxusMenu";
import {
  AgentMemoryFieldNCL,
  BaseToolsFieldNCL,
  KnowledgeBasesFieldNCL,
  FilesFieldNCL,
  DatabasesFieldNCL,
  TablesFieldNCL,
  WorkflowsFieldNCL,
  CoworkersFieldNCL,
  ActionsFieldNCL,
} from "@/components/Assistant/NCL";
import { ExternalValuesField } from "@/components/Assistant/ExternalValues/ExternalValuesField";
import { CoworkerSelectField } from "@/components/Assistant/CoworkerSelectField";
import ToggleButtonField from "@/components/Form/ToggleButtonField";
import {
  SiblingWorkflowVersionField,
  WorkflowVersionField,
} from "@/components/Form/WorkflowVersionField";
import { ProviderApiToggleField } from "@/components/Form/ProviderApiToggleField";
import { SelectArrayDictField } from "@/components/Form/DictSelectArrayField";
import { InputVariableSelectorField } from "@/components/Form/InputVariableSelectorField";
import { DictTextAndSelectArrayField } from "@/components/Form/DictTextAndSelectArrayField";
import { UsersTableField } from "@/components/Modals/AddAppUsersModal";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/shadcn/separator";
import { MenuOption, MenuSelectField } from "@/components/Form/MenuField";
import { ColorSelectField } from "@/components/Form/ColorSelectField";
import { ColorThemeSelectField } from "@/components/Form/ColorThemeSelectField";
import { CardSelectField } from "@/components/Form/CardSelectField";
import {
  AssistantVersionContext,
  AssistantVersionField,
} from "@/components/Form/AssistantVersionField";
import { WorkflowPickerField } from "@/components/Form/WorkflowPickerField";
import { FileTypes, fileTypesForDataType } from "@/utils/mimetypes";
import { TipTapListField } from "@/components/Form/TipTapListField";
import { ArrayFieldWrapper } from "@/components/Form/ArrayFieldWrapper";
import { TypedValueField } from "@/components/Form/TypedValueField";
import { parseFlowInputType } from "@/components/SpotflowV2/flowTypes";
import { AtSign, Check, ExternalLink } from "lucide-react";
import { AdvancedChipOption } from "@/components/Form/TiptapEditor/chipOptionsConfig";
import { ComboboxOption } from "@/components/shadcn/combobox";
interface IBaseConfig {
  config: NodeDefinition["config"];
  type: string;
  id: string;
  getNodeById: (nodeId: string) => any;
  deleteEdgesFromNode: (nodeId: string) => any;
  updateMultiTypeDictArrayConnectors: (
    nodeId: string,
    connectors: string[],
    value: ITypeDictValue[]
  ) => void;
  nodes_fetching_config: string[];
  getDynamicConfigsFromBackend: (
    nodeId?: string
  ) => Promise<NoxusReactflowNode[] | undefined>;
  addNewDynamicOutput: (nodeId: string, connector_name: string) => any;
  removeDynamicOutput: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  changeDynamicOutputName: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  isReadOnly?: boolean;
}

export const BaseConfig = ({
  config,
  id,
  type,
  getNodeById,
  deleteEdgesFromNode,
  updateMultiTypeDictArrayConnectors,
  nodes_fetching_config,
  getDynamicConfigsFromBackend,
  addNewDynamicOutput,
  removeDynamicOutput,
  changeDynamicOutputName,
  unselectAllNodes,
  isReadOnly,
}: IBaseConfig) => {
  let basicConfig = {};
  let advancedConfig = {};

  for (const [key, value] of Object.entries(config)) {
    const node_config = value as NoxusFormEntryConfig;
    if (node_config.advanced) {
      advancedConfig = { ...advancedConfig, [key]: value };
      // } else if (!node_config.hitl && !node_config.safety) {
    } else {
      basicConfig = { ...basicConfig, [key]: value };
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col gap-6 items-stretch">
        {Object.keys(basicConfig).map((key, index) => {
          const node_config = config[key] as NoxusFormEntryConfig;

          if (key === "knowledge_base_name") return <></>;
          if (!node_config.visible) return <></>;

          return (
            <CorrectFieldWrapper
              key={`${id}.${index}`}
              id={id}
              node_type={type}
              field_key={key}
              config={node_config}
              getNodeById={getNodeById}
              deleteEdgesFromNode={deleteEdgesFromNode}
              updateMultiTypeDictArrayConnectors={
                updateMultiTypeDictArrayConnectors
              }
              nodes_fetching_config={nodes_fetching_config}
              getDynamicConfigsFromBackend={getDynamicConfigsFromBackend}
              addNewDynamicOutput={addNewDynamicOutput}
              changeDynamicOutputName={changeDynamicOutputName}
              removeDynamicOutput={removeDynamicOutput}
              unselectAllNodes={unselectAllNodes}
              isReadOnly={isReadOnly}
            />
          );
        })}
      </div>

      {Object.keys(advancedConfig).length > 0 && (
        <div className="mt-6">
          <Accordion
            type="single"
            collapsible
            onClick={() => {
              analytics_track(
                ANALYTICS_EVENTS.WORKFLOW_ADVANCED_CONFIGS_TOGGLED
              );
            }}
          >
            <AccordionItem value="advanced-options">
              <AccordionTrigger>Advanced options</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-6 items-stretch">
                  {Object.keys(advancedConfig).map((key, index) => {
                    const node_config = config[key] as NoxusFormEntryConfig;
                    if (key === "knowledge_base_name") return <></>;
                    return (
                      <CorrectFieldWrapper
                        key={`${id}.${index}`}
                        id={id}
                        node_type={type}
                        field_key={key}
                        config={node_config}
                        getNodeById={getNodeById}
                        deleteEdgesFromNode={deleteEdgesFromNode}
                        updateMultiTypeDictArrayConnectors={
                          updateMultiTypeDictArrayConnectors
                        }
                        nodes_fetching_config={nodes_fetching_config}
                        getDynamicConfigsFromBackend={
                          getDynamicConfigsFromBackend
                        }
                        addNewDynamicOutput={addNewDynamicOutput}
                        changeDynamicOutputName={changeDynamicOutputName}
                        removeDynamicOutput={removeDynamicOutput}
                        unselectAllNodes={unselectAllNodes}
                        isReadOnly={isReadOnly}
                      />
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
};

interface ICorrectField {
  id: string;
  config: NoxusFormEntryConfig;
  field_key: string;
  node_type: string;
  variable_type?: string;
  getNodeById: (nodeId: string) => any;
  deleteEdgesFromNode: (nodeId: string) => any;
  updateMultiTypeDictArrayConnectors: (
    nodeId: string,
    connectors: string[],
    value: ITypeDictValue[]
  ) => void;
  nodes_fetching_config: string[];
  getDynamicConfigsFromBackend: (
    nodeId?: string
  ) => Promise<NoxusReactflowNode[] | undefined>;
  addNewDynamicOutput: (nodeId: string, connector_name: string) => any;
  removeDynamicOutput: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  changeDynamicOutputName: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  hasPrecheckedVisibility?: boolean;
  isReadOnly?: boolean;
  validation?: ValidationResponse;
  chipOptions?: AdvancedChipOption[];
  hasPortal?: boolean;
  clearValue?: string | null;
}

export const CorrectFieldWrapper = (props: ICorrectField) => {
  const form = useFormContext();
  const { isEditorV2 } = useContext(ConfigFormRendererContext);

  const checkVisibilityRule = useCallback(
    (rule: VisibleIf | VisibleIfNot | VisibleIfGroup): boolean => {
      if (props.hasPrecheckedVisibility) {
        return true;
      }
      return evaluateVisibilityRule(rule, (configSource) =>
        form.watch(`${props.id}.${configSource}`)
      );
    },
    [form, props.hasPrecheckedVisibility, props.id]
  );

  const shouldRenderField = useMemo(() => {
    return props.config?.rules?.every((r) => {
      if (
        r.type === "visible_if" ||
        r.type === "visible_if_group" ||
        r.type === "visible_if_not"
      ) {
        return checkVisibilityRule(
          r as VisibleIf | VisibleIfGroup | VisibleIfNot
        );
      }
      return true;
    });
  }, [props.config?.rules, checkVisibilityRule]);

  const { error, warning, info } = useMemo(() => {
    return {
      error: props.validation?.errors?.[props.field_key]?.detail,
      warning: props.validation?.warnings?.[props.field_key]?.detail,
      info: props.validation?.infos?.[props.field_key]?.detail,
    };
  }, [props.validation, props.field_key]);

  // Only render the component if all rules that can affect visibility are true
  if (!shouldRenderField) {
    return <></>;
  }

  const shouldDisplayErrors = error || warning || info;

  return (
    <div className={cn(props.config.display?.is_subconfig && "-mt-2")}>
      <ErrorBoundary
        fallback={
          <ErrorBoundaryLabel
            label={props.config.display?.label || ""}
            node_id={props.id}
          />
        }
      >
        <CorrectField {...props} />
        {shouldDisplayErrors && (
          <div className="mt-2 flex flex-col gap-2">
            {error && (
              <Banner
                label={error}
                style={isEditorV2 ? "warning" : "error"}
                size="sm"
              />
            )}
            {warning && <Banner label={warning} style="warning" size="sm" />}
            {info && <Banner label={info} style="info" size="sm" />}
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};

const ErrorBoundaryLabel = ({
  label,
  node_id,
}: {
  label: string;
  node_id: string;
}) => {
  return (
    <div>
      <NoxusFormLabel label={label} />
      <Banner
        label={
          <p>
            There was an error rendering this configuration. The team has been
            notified. If this problem persists, please{" "}
            <span
              className="underline cursor-pointer"
              onClick={() =>
                triggerPylonMessage(
                  `An error happened while rendering the config "${label}" on the node ${node_id}.`
                )
              }
            >
              contact support
            </span>
            .
          </p>
        }
        style="error"
        size="sm"
      />
    </div>
  );
};
export const CorrectField = ({
  id,
  config,
  field_key,
  node_type,
  variable_type,
  getNodeById,
  deleteEdgesFromNode,
  updateMultiTypeDictArrayConnectors,
  nodes_fetching_config,
  getDynamicConfigsFromBackend,
  addNewDynamicOutput,
  changeDynamicOutputName,
  removeDynamicOutput,
  unselectAllNodes,
  isReadOnly,
  chipOptions,
  hasPortal,
  clearValue,
}: ICorrectField) => {
  const { isEditorV2 } = useContext(ConfigFormRendererContext);
  const form = useFormContext();
  const node = getNodeById(id);
  const editorInputs = useEditorInputs();

  const rules = config?.rules;

  const isDisabled = useMemo(() => {
    if (isReadOnly) return true;

    const disabled_if = rules?.find((r) => r.type == "disabled_if") as
      | DisabledIf
      | undefined;

    if (disabled_if == undefined) return false;

    let config_value;
    if (id) {
      config_value = form.watch(`${id}.${disabled_if.config_source}`);
    } else {
      config_value = form.watch(disabled_if.config_source);
    }

    return disabled_if.value === config_value;
  }, [rules, form, id, isReadOnly]);

  const buttonValues = useMemo(() => {
    const variable_connectors = node?.data.config.inputs?.filter(
      (input: any) =>
        (input.type === "variable_connector" ||
          input.type === "variable_type_size_connector") &&
        input.visible
    );

    if (!variable_connectors) {
      return [];
    }
    return variable_connectors?.reduce(
      (acc: string[], input: VariableConnector | VariableTypeSizeConnector) => {
        const keys = input.keys || [];
        return [...acc, ...keys];
      },
      [] as string[]
    );
  }, [node?.data.config.inputs]) as string[];

  const advancedChipOptions: AdvancedChipOption[] = useMemo(() => {
    const valueGroups = chipOptions ?? [
      {
        label: "Values",
        color: "blue" as const,
        values: buttonValues,
      },
    ];
    const allowReferenceMentions = (config.display as any)
      ?.allow_reference_mentions;
    if (!allowReferenceMentions) {
      return valueGroups.filter((group) => group.prefix !== "@");
    }
    if (valueGroups.some((group) => group.prefix === "@")) {
      return valueGroups;
    }
    return [
      ...valueGroups,
      {
        label: "References",
        icon: <AtSign className="w-4 h-4" />,
        color: "red",
        prefix: "@",
        values: valueGroups
          .filter((group) => group.prefix !== "@")
          .flatMap((group) => group.values),
      },
    ];
  }, [buttonValues, chipOptions, config.display]);

  const outputValues = useMemo(() => {
    const variable_connectors = node?.data.config.outputs?.filter(
      (output: any) =>
        output.type === "variable_connector" ||
        output.type === "variable_type_size_connector"
    );

    if (!variable_connectors) {
      return [];
    }
    return variable_connectors?.reduce(
      (
        acc: string[],
        output: VariableConnector | VariableTypeSizeConnector
      ) => {
        const keys = output.keys || [];
        return [...acc, ...keys];
      },
      [] as string[]
    );
  }, [node?.data.config.outputs]) as string[];

  // const isLoading = nodes_fetching_config.includes(id);
  const isLoading = false;

  if (!config?.display?.type) {
    return (
      <NoxusInputField
        name={`${id}.${field_key}`}
        control={form.control}
        errors={form.formState.errors}
        label={getLabel(field_key, node_type)}
        key={`${id}.${field_key}`}
        rules={{ required: !config.optional }}
        disable={isLoading}
      />
    );
  }

  if (!config.visible) {
    return null;
  }

  let idFieldKey;
  if (!!id && !!field_key) {
    idFieldKey = `${id}.${field_key}`;
  } else {
    idFieldKey = field_key;
  }

  const defaultFieldFallback = (
    <NoxusInputField
      name={idFieldKey}
      control={form.control}
      errors={form.formState.errors}
      label={config.display.label || undefined}
      disable={isLoading || isDisabled}
      key={`${id}.${field_key}`}
      rules={{ required: !config.optional }}
    />
  );

  switch (config.display.type) {
    case "hidden":
      // Persisted value with no rendered control; managed elsewhere (e.g. a
      // builder dialog writing a sibling config key).
      return null;
    case "config_workflow_version": {
      const workflow_version_display = config.display as ConfigWorkflowVersion;
      const versionFieldProps = {
        name: idFieldKey,
        control: form.control,
        label: workflow_version_display.label,
        isDisabled,
        allowCurrentVersion: true,
        warnOnCurrentVersion: workflow_version_display.warn_on_current ?? true,
        mapLegacyCurrent: true,
      };
      if (workflow_version_display.workflow_field) {
        const siblingName = idFieldKey.includes(".")
          ? [
              ...idFieldKey.split(".").slice(0, -1),
              workflow_version_display.workflow_field,
            ].join(".")
          : workflow_version_display.workflow_field;
        return (
          <SiblingWorkflowVersionField
            workflowFieldName={siblingName}
            {...versionFieldProps}
          />
        );
      }
      return <WorkflowVersionField {...versionFieldProps} />;
    }
    case "workflow_picker":
      const workflow_picker_display = config.display as ConfigWorkflowPicker;
      return (
        <WorkflowPickerField
          name={idFieldKey}
          control={form.control}
          label={workflow_picker_display.label}
          tooltip={workflow_picker_display.tooltip || undefined}
          workflowId={workflow_picker_display.workflow_id}
          workflowName={workflow_picker_display.workflow_name}
          workflowIcon={workflow_picker_display.workflow_icon}
          workflowType={
            workflow_picker_display.workflow_type || "task_automation"
          }
          emptyLabel={
            workflow_picker_display.empty_label || "No automation flow"
          }
          isDisabled={isDisabled}
          defaultValue={config.default}
        />
      );
    case "config_assistant_version":
      const assistant_version_display =
        config.display as ConfigAssistantVersion;

      return (
        <AssistantVersionContext.Provider
          value={assistant_version_display.assistant_id || undefined}
        >
          <AssistantVersionField
            name={idFieldKey}
            control={form.control}
            label={assistant_version_display.label}
            isDisabled={isDisabled}
            allowCurrentVersion={true}
            disablePortal={true}
          />
        </AssistantVersionContext.Provider>
      );

    case "divider":
      const divider_display = config.display as ConfigDivider;
      const showLine = divider_display.show_line !== false; // Default to true
      const spacing = divider_display.spacing || 0;

      // Map spacing values to Tailwind classes
      const spacingClasses: Record<number, string> = {
        0: "my-0",
        1: "my-1",
        2: "my-2",
        3: "my-3",
        4: "my-4",
        5: "my-5",
        6: "my-6",
        8: "my-8",
        10: "my-10",
        12: "my-12",
        16: "my-16",
      };

      return (
        <div className={spacingClasses[spacing] || "my-0"}>
          {showLine && <Separator />}
        </div>
      );

    case "banner":
      const banner_display = config.display as ConfigBanner;
      return (
        <Banner
          style={banner_display.style}
          size="sm"
          label={
            <div>
              {banner_display.title && (
                <p className="font-semibold">{banner_display.title}</p>
              )}
              {banner_display.label && (
                <p className="whitespace-pre-line">{banner_display.label}</p>
              )}
              {banner_display.href_label && banner_display.href_url && (
                <a
                  href={banner_display.href_url}
                  target="_blank"
                  className="block underline mt-1"
                  rel="noreferrer noopener"
                >
                  {banner_display.href_label}
                </a>
              )}
            </div>
          }
        />
      );

    case "integration_picker":
      const integration_picker_display =
        config.display as ConfigIntegrationPicker;
      return (
        <IntegrationPickerField
          name={idFieldKey}
          control={form.control}
          label={integration_picker_display.label}
          tooltip={integration_picker_display.tooltip || undefined}
          integrations={integration_picker_display.integrations}
          isDisabled={isDisabled}
          hasMockedInfo={integration_picker_display.has_mocked_info}
        />
      );

    case "credential_picker":
      const credential_picker_display =
        config.display as ConfigCredentialPicker;
      return (
        <CredentialPickerField
          name={idFieldKey}
          control={form.control}
          nodeId={id}
          integrationId={credential_picker_display.integration_type}
          label={credential_picker_display.label}
          isDisabled={isDisabled}
          nodeType={node_type}
        />
      );

    case "ui_text_display":
      const text_display_config = config.display as ConfigTextDisplay;

      return (
        <div>
          {text_display_config.label && (
            <NoxusFormLabel
              label={text_display_config.label}
              tooltip={text_display_config.tooltip || ""}
            />
          )}
          {text_display_config.title && (
            <p className="text-md font-medium">{text_display_config.title}</p>
          )}
          {text_display_config.text && (
            <p className="text-sm text-muted-foreground leading-normal">
              {text_display_config.text}
            </p>
          )}
          {text_display_config.small_text && (
            <p className="text-xs text-muted-foreground leading-normal">
              {text_display_config.small_text}
            </p>
          )}
        </div>
      );

    case "copyable_url":
      const copyable_url_display = config.display as ConfigCopyableUrl;
      return (
        <CopyableUrlField
          label={copyable_url_display.label}
          url={copyable_url_display.url || ""}
          description={copyable_url_display.description}
        />
      );

    case "field_badge":
      const field_badge_display = config.display as ConfigFieldBadge;
      return (
        <FieldBadgeField
          sourceField={field_badge_display.source_field}
          label={field_badge_display.label}
          tooltip={field_badge_display.tooltip}
        />
      );

    case "big_text_variables":
    case "rich_text_variables": {
      const rich_text_display = config.display as ConfigRichTextVariables;
      const chipOptionLabels = (node?.data as any)?.chipOptionLabels as
        | Record<string, string>
        | undefined;
      return (
        <RichTextField
          options={advancedChipOptions}
          control={form.control}
          name={idFieldKey}
          label={rich_text_display.label}
          tooltip={rich_text_display.tooltip || undefined}
          unselectAllNodes={unselectAllNodes}
          placeholder={rich_text_display.placeholder || undefined}
          config={{
            isExpandable: true,
            hideToolbar:
              rich_text_display.hide_toolbar ||
              rich_text_display.is_simple_text ||
              false,
            numberLines: rich_text_display.number_of_lines || undefined,
            noSpaceOnInsert: rich_text_display.no_space_on_insert || false,
            hideChipOptions: rich_text_display.hide_chip_options || false,
          }}
          isControlled={true}
          isSimpleText={rich_text_display.is_simple_text}
          isDisabled={isDisabled}
        />
      );
    }

    case "rich_text_variables_ai": {
      const rich_text_display_ai = config.display as ConfigRichTextVariablesAI;
      // Markdown-backed prompts use the markdown editor instead of the
      // TipTap-based one: every Editor V2 field, plus V7's prompt (a plain
      // markdown string with `:var[name]` placeholders) in the V1 editor. V6
      // stays on the rich-text editor because its prompt is a list of
      // NoxusTipTapDocument objects.
      if (isEditorV2 && rich_text_display_ai.enable_heuristics) {
        return (
          <AIComplexMarkdownField
            control={form.control}
            name={idFieldKey}
            label={rich_text_display_ai.label}
            tooltip={rich_text_display_ai.tooltip || undefined}
            placeholder={rich_text_display_ai.placeholder || undefined}
            isDisabled={isDisabled}
            inputs={editorInputs}
            nodeId={id}
            improverPrompt={rich_text_display_ai.improver_prompt}
            enableHeuristics={rich_text_display_ai.enable_heuristics}
            heuristicContext={
              rich_text_display_ai.heuristic_context || undefined
            }
            heuristicTypes={rich_text_display_ai.heuristic_types || undefined}
            improverContext={rich_text_display_ai.improver_context || undefined}
          />
        );
      }
      if (isEditorV2) {
        return (
          <ComplexMarkdownField
            control={form.control}
            name={idFieldKey}
            label={rich_text_display_ai.label}
            tooltip={rich_text_display_ai.tooltip || undefined}
            placeholder={rich_text_display_ai.placeholder || undefined}
            isDisabled={isDisabled}
            inputs={editorInputs}
            nodeId={id}
          />
        );
      }
      const chipOptionLabelsAI = (node?.data as any)?.chipOptionLabels as
        | Record<string, string>
        | undefined;
      const chipOptionMetaAI = (node?.data as any)?.chipOptionMeta as
        | Record<string, { image?: string; color?: string; nodeName?: string }>
        | undefined;
      // Only show chips for keys that have a variable binding (i.e. exist in chipOptionLabels)
      const boundChipOptions = chipOptionLabelsAI
        ? advancedChipOptions.map((group) => ({
            ...group,
            values: group.values
              .filter((v) => {
                const key = typeof v === "string" ? v : v.value;
                return key in chipOptionLabelsAI;
              })
              .map((v) => {
                const key = typeof v === "string" ? v : v.value;
                const meta = chipOptionMetaAI?.[key];
                // Only enrich when we have a real source node (nodeName set).
                // Globals are intentionally left with the legacy chip look.
                if (!meta?.nodeName) return v;
                const base = typeof v === "string" ? { value: v, label: v } : v;
                return {
                  ...base,
                  sourceImage: meta.image,
                  sourceColor: meta.color,
                  sourceName: meta.nodeName,
                };
              }),
          }))
        : advancedChipOptions;
      return (
        <RichTextFieldAI
          options={boundChipOptions}
          control={form.control}
          name={idFieldKey}
          label={rich_text_display_ai.label}
          tooltip={rich_text_display_ai.tooltip || undefined}
          unselectAllNodes={unselectAllNodes}
          placeholder={rich_text_display_ai.placeholder || undefined}
          isControlled={true}
          isDisabled={isDisabled}
          improverPrompt={rich_text_display_ai.improver_prompt}
          enableHeuristics={rich_text_display_ai.enable_heuristics}
          heuristicContext={rich_text_display_ai.heuristic_context || undefined}
          heuristicTypes={rich_text_display_ai.heuristic_types || undefined}
          improverContext={rich_text_display_ai.improver_context || undefined}
        />
      );
    }

    case "big_text":
      const big_text_display = config.display as ConfigBigText;
      return (
        <RichTextField
          control={form.control}
          name={idFieldKey}
          label={big_text_display.label}
          placeholder={big_text_display.placeholder || undefined}
          tooltip={big_text_display.tooltip || undefined}
          config={{
            isExpandable: true,
            hideToolbar: big_text_display.hide_toolbar || false,
          }}
          helperText={config.description || undefined}
          isDisabled={isDisabled}
          templateOptions={big_text_display.template_options || undefined}
          isControlled={true}
          options={advancedChipOptions}
        />
      );

    case "tip_tap_list":
      const tip_tap_list_display = config.display as ConfigTipTapList;
      return (
        <TipTapListField
          name={idFieldKey}
          control={form.control}
          label={tip_tap_list_display.label}
          placeholder={tip_tap_list_display.placeholder || undefined}
          number_of_lines={tip_tap_list_display.number_of_lines || undefined}
          options={advancedChipOptions}
          isDisabled={isDisabled}
          required={!config.optional}
        />
      );

    case "gdrive_picker":
      const google_picker_display = config.display as ConfigGdrivePicker;
      // Prefer the declared `connection` picker; fall back to the legacy field.
      const gdriveCredentialId = (form.watch(`${id}.connection`) ||
        form.watch(`${id}._credential_id_gdrive`)) as string | undefined;

      return (
        <GoogleDrivePickerField
          label={google_picker_display.label}
          name={idFieldKey}
          canMultiselect={google_picker_display.multi_select}
          canSelectDirectories={google_picker_display.file_type?.includes(
            "folder"
          )}
          allowedFiles={google_picker_display.file_type || undefined}
          control={form.control}
          defaultValue={form.getValues(idFieldKey)}
          rules={{ required: !config.optional }}
          setIncludeFolders={google_picker_display.setIncludeFolders}
          isDisabled={isDisabled}
          credentialId={gdriveCredentialId}
        />
      );

    case "onedrive_picker":
      const onedrive_picker_display = config.display as ConfigOneDrivePicker;
      // Derive integration type from the service name
      const onedriveIntegrationType =
        onedrive_picker_display.name === "Sharepoint"
          ? "sharepoint"
          : "onedrive";
      const onedriveCredentialId = (form.watch(`${id}.connection`) ||
        form.watch(`${id}._credential_id_${onedriveIntegrationType}`)) as
        | string
        | undefined;

      return (
        <OneDrivePickerField
          label={onedrive_picker_display.label}
          name={idFieldKey}
          canMultiselect={onedrive_picker_display.multi_select}
          canSelectDirectories={onedrive_picker_display.file_type?.includes(
            "folder"
          )}
          allowedFiles={onedrive_picker_display.file_type || undefined}
          control={form.control}
          defaultValue={form.getValues(idFieldKey)}
          isHideLink={["DriveWriterNode", "DriveFolderDownloaderNode"].includes(
            node_type
          )}
          setIncludeFolders={onedrive_picker_display.set_include_folders}
          baseUrl={onedrive_picker_display.base_url || undefined}
          serviceName={onedrive_picker_display.name}
          rules={{ required: !config.optional }}
          isDisabled={isDisabled}
          credentialId={onedriveCredentialId}
        />
      );
    case "number_range":
      const number_range_display = config.display as ConfigNumberRange;
      return (
        <NumberInputField
          control={form.control}
          name={idFieldKey}
          defaultValue={config.default as number | undefined}
          min={number_range_display.min}
          max={number_range_display.max}
          step={
            number_range_display.step ?? (config.type === "float" ? "any" : 1)
          }
          label={number_range_display.label}
          tooltip={number_range_display.tooltip || undefined}
          helperText={config.description || undefined}
          isDisabled={isLoading || isDisabled}
        />
      );
    case "number":
      const number_display = config.display as ConfigNumber;
      return (
        <NumberInputField
          control={form.control}
          name={idFieldKey}
          defaultValue={config.default as number | undefined}
          label={number_display.label}
          tooltip={number_display.tooltip || undefined}
          isDisabled={isLoading || isDisabled}
          helperText={config.description || undefined}
          step={config.type === "float" ? "any" : 1}
          integer={config.type === "int"}
        />
      );
    case "string_list":
      const stringListValue: string[] = form.watch(idFieldKey) || [];
      return (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">{config.display.label}</label>
          <div className="flex flex-col gap-2">
            {stringListValue.map((item: string, idx: number) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  value={item}
                  onChange={(e) => {
                    const updated = [...stringListValue];
                    updated[idx] = e.target.value;
                    form.setValue(idFieldKey, updated, { shouldDirty: true });
                  }}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder={(config.display as any).placeholder || ""}
                  disabled={isLoading || isDisabled}
                />
                <button
                  type="button"
                  onClick={() => {
                    const updated = stringListValue.filter(
                      (_: string, i: number) => i !== idx
                    );
                    form.setValue(idFieldKey, updated, { shouldDirty: true });
                  }}
                  className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-md border border-input text-muted-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
                  disabled={isLoading || isDisabled}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                form.setValue(idFieldKey, [...stringListValue, ""], {
                  shouldDirty: true,
                })
              }
              className="flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-input text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
              disabled={isLoading || isDisabled}
            >
              Add entry
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
            </button>
          </div>
        </div>
      );
    case "viewport_size":
      const viewportValue = form.watch(idFieldKey) || {
        width: 1280,
        height: 720,
      };
      return (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <label className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              W
            </label>
            <input
              type="number"
              value={viewportValue.width ?? 1280}
              onChange={(e) =>
                form.setValue(
                  idFieldKey,
                  { ...viewportValue, width: parseInt(e.target.value) || 0 },
                  { shouldDirty: true }
                )
              }
              className="flex h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              disabled={isLoading || isDisabled}
            />
          </div>
          <span className="text-sm text-muted-foreground shrink-0">×</span>
          <div className="relative flex-1">
            <label className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              H
            </label>
            <input
              type="number"
              value={viewportValue.height ?? 720}
              onChange={(e) =>
                form.setValue(
                  idFieldKey,
                  { ...viewportValue, height: parseInt(e.target.value) || 0 },
                  { shouldDirty: true }
                )
              }
              className="flex h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              disabled={isLoading || isDisabled}
            />
          </div>
        </div>
      );
    case "number_slider":
      const number_slider_display = config.display as ConfigNumberSlider;
      return (
        <NumberSliderField
          control={form.control}
          name={idFieldKey}
          defaultValue={config.default as number | undefined}
          label={number_slider_display.label}
          tooltip={number_slider_display.tooltip || undefined}
          isDisabled={isLoading || isDisabled}
          min={number_slider_display.min}
          max={number_slider_display.max}
          step={number_slider_display.step}
          hideNumber={number_slider_display.hide_number}
          hideInput={number_slider_display.hide_input}
          helperText={config.description || undefined}
        />
      );
    case "number_range_slider":
      const number_range_slider_display =
        config.display as ConfigNumberRangeSlider;
      return (
        <NumberRangeSliderField
          control={form.control}
          name={idFieldKey}
          defaultValue={config.default as [number, number] | undefined}
          label={number_range_slider_display.label}
          tooltip={number_range_slider_display.tooltip || undefined}
          isDisabled={isLoading || isDisabled}
          min={number_range_slider_display.min}
          max={number_range_slider_display.max}
          step={number_range_slider_display.step}
          hideNumber={number_range_slider_display.hide_number}
          helperText={config.description || undefined}
        />
      );
    case "config_complex_condition":
      const complex_condition_display =
        config.display as ConfigComplexCondition;
      return (
        <ComplexConditionalField
          control={form.control}
          name={idFieldKey}
          defaultValue={config.default}
          label={complex_condition_display.label}
          tooltip={complex_condition_display.tooltip || undefined}
          input_options={advancedChipOptions}
          isDisabled={isDisabled}
        />
      );
    case "select_type":
      const select_type_display = config.display as ConfigTypeSelect;
      const options = select_type_display.values.map((value: any) => {
        return {
          value,
          label: value,
        };
      });

      return (
        <HorizontalSelectField
          name={idFieldKey}
          label={select_type_display.label}
          options={options}
          control={form.control}
          defaultValue={config.default}
          onChange={(value: string | string[]) => {
            const filteredRules = config.rules?.filter(
              (r) => r.type === "sets_connector_type"
            );
            if (filteredRules && filteredRules.length > 0) {
              deleteEdgesFromNode(id);
            }
          }}
          isDisabled={isLoading || isDisabled}
        />
      );
    case "select_chips":
      const select_chips_display = config.display as ConfigChipsSelect;
      const select_chips_options = select_chips_display.values.map(
        (option: any) => {
          // Type chips (`str`, `list[File]`, …) carry an icon; list types show
          // the element icon stacked. Non-type chips (e.g. weekday selects)
          // have no known icon and render label-only, as before.
          const { dataType, isList } = parseFlowInputType(
            option.value as string
          );
          return {
            value: option.value,
            label: option.label,
            icon: hasTypeIcon(dataType) ? (
              <DataTypeIcon dataType={dataType} isList={isList} size={16} />
            ) : undefined,
          };
        }
      ) as OptionType[];

      // Multi-select chips (e.g. weekly schedule "Days of Week") must store a
      // list. SpotSelectField is single-only and would post a bare scalar, which
      // the backend list[str] fields reject — so route is_multi to the real
      // multi-select.
      if (select_chips_display.is_multi) {
        return (
          <MultiSelectField
            name={idFieldKey}
            label={select_chips_display.label || ""}
            tooltip={select_chips_display.tooltip || undefined}
            control={form.control}
            options={select_chips_options}
            helperText={config.description || undefined}
            required={!config.optional}
            hasStar={!config.optional}
            isDisabled={isLoading || isDisabled}
          />
        );
      }

      return (
        <SpotSelectField
          label={select_chips_display.label || undefined}
          name={idFieldKey}
          defaultValue={config.default || undefined}
          control={form.control}
          options={select_chips_options}
          rules={{ required: !config.optional }}
          // defaultValue={config.default}
          // endpoint={select_chips_display?.endpoint || undefined}
          // canWrap={select_chips_display.can_wrap}
          isDisabled={isLoading || isDisabled}
          hasPortal={hasPortal}
        />
      );
    case "config_dynamic_select": {
      const dynamic_display = config.display as ConfigDynamicSelect;
      // The source sibling lives at the same form-path prefix as this
      // field (e.g. "settings_.embedding_model" for "settings_.embedding_dimension").
      const lastDot = idFieldKey.lastIndexOf(".");
      const prefix = lastDot >= 0 ? idFieldKey.slice(0, lastDot + 1) : "";
      return (
        <DynamicSelectField
          name={idFieldKey}
          control={form.control}
          sourceField={prefix + dynamic_display.source_field}
          sourceAttribute={dynamic_display.source_attribute}
          endpoint={dynamic_display.endpoint}
          fallbackValues={
            dynamic_display.fallback_values as
              | (string | number | ComboboxOption)[]
              | undefined
          }
          label={dynamic_display.label}
          helperText={config.description || undefined}
          placeholder={dynamic_display.placeholder || undefined}
          tooltip={dynamic_display.tooltip || undefined}
          isClearable={dynamic_display.is_clearable}
          isNotSearchable={dynamic_display.is_searchable === false}
          isDisabled={!!dynamic_display.is_disabled || isDisabled}
          isOptional={config.optional}
          hasPortal={hasPortal}
        />
      );
    }
    case "kb_picker":
      return (
        <KBPickerField
          name={idFieldKey}
          control={form.control}
          label={config.display.label}
          isDisabled={isDisabled}
        />
      );
    case "tool_select":
      return (
        <ToolSelectField
          name={idFieldKey}
          control={form.control}
          label={config.display.label}
          tooltip={config.display.tooltip}
          isDisabled={isDisabled}
        />
      );
    case "select":
      const select_display = config.display as ConfigSelect;
      const raw_select_options =
        select_display.values[0] && typeof select_display.values[0] === "string"
          ? select_display.values.map((value) => ({
              label: value as string,
              value: value as string,
            }))
          : (select_display.values as OptionType[]);

      // Map well-known string `icon` hints to lucide components so backend-
      // emitted dynamic options can render with native icons (e.g. the
      // GetSecretNode dropdown emits "key" / "braces" per variable kind).
      const SELECT_ICON_MAP: Record<string, typeof KeyRound> = {
        key: KeyRound,
        braces: Braces,
      };
      const select_options: OptionType[] = (
        raw_select_options as OptionType[]
      ).map((opt) => {
        if (select_display.icons && typeof opt.value === "string") {
          const Glyph = WORKFLOW_ICONS[opt.value]?.icon;
          return Glyph
            ? {
                ...opt,
                icon: <Glyph className="h-4 w-4 text-muted-foreground" />,
              }
            : opt;
        }
        if (typeof opt.icon !== "string") return opt;
        const IconComp = SELECT_ICON_MAP[opt.icon];
        if (!IconComp) {
          // A data-type key ("str", "object", "Image") renders the same icon
          // the type chips use, so a type-flavoured select matches them.
          return hasTypeIcon(opt.icon)
            ? { ...opt, icon: <DataTypeIcon dataType={opt.icon} size={16} /> }
            : opt;
        }
        return {
          ...opt,
          icon: (
            <IconComp
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          ),
        };
      });

      return (
        <SpotSelectField
          label={select_display.label}
          name={idFieldKey}
          defaultValue={config.default || undefined}
          control={form.control}
          helperText={config.description || undefined}
          placeholder={select_display.placeholder || "Pick one option"}
          options={select_options}
          isLoading={isLoading}
          tooltip={select_display.tooltip || undefined}
          isDisabled={!!select_display.is_disabled || isDisabled}
          isClearable={select_display.is_clearable}
          clearValue={clearValue}
          isHorizontal={select_display.is_horizontal}
          isNotSearchable={select_display.is_searchable === false}
          isOptional={config.optional}
          showOptionalLabel={select_display.show_optional_label}
          needsConfirmation={select_display.needs_confirmation}
          confirmationMessage={select_display.confirmation_message || undefined}
          errorMessage={select_display.error || undefined}
          hasPortal={hasPortal}
        />
      );
    case "menu_select":
      const menu_display = config.display as ConfigMenuSelect;
      const menu_options =
        menu_display.values[0] && typeof menu_display.values[0] === "string"
          ? menu_display.values.map((value: any) => ({
              value: value as string,
              label: value as string,
            }))
          : (menu_display.values.map((value: any) => {
              const IconComp =
                typeof value.icon === "string"
                  ? iconMap[value.icon as keyof typeof iconMap]
                  : undefined;
              return {
                value: value.value,
                label: value.label,
                description: value.description,
                icon: IconComp ? (
                  <IconComp
                    size="0.875rem"
                    className="text-muted-foreground shrink-0"
                  />
                ) : (
                  value.icon
                ),
              };
            }) as MenuOption[]);

      return (
        <MenuSelectField
          label={menu_display.label || undefined}
          name={idFieldKey}
          defaultValue={config.default || undefined}
          control={form.control}
          helperText={config.description || undefined}
          options={menu_options}
          tooltip={menu_display.tooltip || undefined}
          isDisabled={!!menu_display.is_disabled || isDisabled}
          isHorizontal={menu_display.is_horizontal}
          buttonLabel={menu_display.button_label || undefined}
          buttonVariant={menu_display.button_variant}
          buttonSize={menu_display.button_size}
          buttonIcon={menu_display.button_icon || undefined}
          needsConfirmation={menu_display.needs_confirmation}
          confirmationMessage={menu_display.confirmation_message || undefined}
          mixpanelEvent={menu_display?.mixpanel_event || undefined}
        />
      );
    case "model_select":
      const model_select_display = config.display as ConfigModelSelect;
      return (
        <ModelSelectField
          name={idFieldKey}
          endpoint={model_select_display.endpoint}
          label={model_select_display.label}
          modelType={model_select_display.model_type}
          defaultValue={config.default as string | undefined}
          required={!config.optional}
          isDisabled={isDisabled}
          maxModels={model_select_display.model_limit || undefined}
          showPresets={model_select_display.show_presets !== false}
          showInlinePresets={model_select_display.show_inline_presets !== false}
          layout={model_select_display.layout || "side_by_side"}
          hideDisabledModels={
            model_select_display.hide_disabled_models === true
          }
          defaultPreset={model_select_display.default_preset ?? undefined}
        />
      );
    case "model_toggle":
      const model_toggle_display = config.display as ConfigModelToggle;
      return (
        <ModelToggleField
          name={idFieldKey}
          endpoint={model_toggle_display.endpoint}
          label={model_toggle_display.label}
          modelType={model_toggle_display.model_type}
          defaultValue={
            config.default
              ? Array.isArray(config.default)
                ? config.default
                : [config.default]
              : undefined
          }
          required={!config.optional}
          isDisabled={!!model_toggle_display.is_disabled || isDisabled}
        />
      );
    case "text":
      const text_display = config.display as ConfigText;

      if (text_display.is_code) {
        return (
          <CodeField
            name={idFieldKey}
            label={text_display.label}
            placeholder={text_display.placeholder || ""}
            inputValues={buttonValues}
            outputValues={outputValues}
            isDisabled={isDisabled}
            helperText={config.description || undefined}
          />
        );
      } else {
        const textField = (
          <NoxusInputField
            name={idFieldKey}
            label={config.supports_from_secret ? undefined : text_display.label}
            helperText={
              config.supports_from_secret
                ? undefined
                : config.description || undefined
            }
            placeholder={text_display.placeholder || undefined}
            tooltip={
              config.supports_from_secret
                ? undefined
                : text_display.tooltip || undefined
            }
            key={idFieldKey}
            rules={{
              required: !config.optional,
              ...(text_display.max_length
                ? {
                    maxLength: {
                      value: text_display.max_length,
                      message: `Maximum ${text_display.max_length} characters`,
                    },
                  }
                : {}),
            }}
            control={form.control}
            defaultValue={config.default}
            disable={
              isLoading || text_display.is_disabled || isDisabled || undefined
            }
            isReadOnly={
              isLoading || text_display.is_disabled || isDisabled || undefined
            }
            isTextarea={text_display.is_textarea}
            fetchingDisplayOptions={
              text_display.show_refresh
                ? {
                    onClick: getDynamicConfigsFromBackend,
                    isLoading: nodes_fetching_config.includes(id),
                  }
                : undefined
            }
            customStyle={cn(
              text_display.class_name,
              config.supports_from_secret && "pr-10"
            )}
            maxLength={text_display.max_length || undefined}
          />
        );
        if (config.supports_from_secret) {
          return (
            <WorkspaceSecretRefField
              name={idFieldKey}
              label={text_display.label}
              helperText={config.description}
              tooltip={text_display.tooltip}
              required={!config.optional}
              disabled={
                isLoading || text_display.is_disabled || isDisabled || undefined
              }
            >
              {textField}
            </WorkspaceSecretRefField>
          );
        }
        return textField;
      }
    case "password": {
      const password_display = config.display as ConfigPassword;
      const passwordField = (
        <NoxusInputField
          name={idFieldKey}
          label={
            config.supports_from_secret ? undefined : password_display.label
          }
          helperText={
            config.supports_from_secret
              ? undefined
              : password_display.help_text || config.description || undefined
          }
          placeholder={password_display.placeholder || undefined}
          tooltip={
            config.supports_from_secret
              ? undefined
              : password_display.tooltip || undefined
          }
          key={idFieldKey}
          rules={{ required: !config.optional }}
          control={form.control}
          defaultValue={config.default}
          disable={
            isLoading || password_display.is_disabled || isDisabled || undefined
          }
          isOptional={!config.supports_from_secret && config.optional}
          showOptionalLabel={!config.supports_from_secret && config.optional}
          type="password"
          customStyle={config.supports_from_secret ? "pr-16" : undefined}
        />
      );
      if (config.supports_from_secret) {
        return (
          <WorkspaceSecretRefField
            name={idFieldKey}
            label={password_display.label}
            helperText={password_display.help_text || config.description}
            tooltip={password_display.tooltip}
            required={!config.optional}
            disabled={
              isLoading ||
              password_display.is_disabled ||
              isDisabled ||
              undefined
            }
            rightOffsetPx={32}
          >
            {passwordField}
          </WorkspaceSecretRefField>
        );
      }
      return passwordField;
    }
    case "text_code_ai":
      const text_code_ai_display = config.display as ConfigTextCodeAI;
      return (
        <CodeFieldAI
          name={idFieldKey}
          label={text_code_ai_display.label}
          placeholder={text_code_ai_display.placeholder || ""}
          isDisabled={isDisabled}
          inputVariablesFieldName={`${id}.${text_code_ai_display.input_variables_config}`}
          outputVariablesFieldName={`${id}.${text_code_ai_display.output_variables_config}`}
          filesAsObjectsFieldName={
            text_code_ai_display.files_as_objects_config
              ? `${id}.${text_code_ai_display.files_as_objects_config}`
              : undefined
          }
          helperText={config.description || undefined}
        />
      );
    case "api_key": {
      const api_key_display = config.display as APIKeyField;
      const apiKeyField = (
        <NoxusInputField
          name={idFieldKey}
          label={
            config.supports_from_secret ? undefined : api_key_display.label
          }
          helperText={
            config.supports_from_secret
              ? undefined
              : config.description || undefined
          }
          placeholder="Enter your API key"
          tooltip={
            config.supports_from_secret
              ? undefined
              : api_key_display.tooltip || undefined
          }
          key={idFieldKey}
          rules={{ required: !config.optional }}
          control={form.control}
          defaultValue={config.default}
          disable={
            isLoading || api_key_display.is_disabled || isDisabled || undefined
          }
          type="password"
          customStyle={config.supports_from_secret ? "pr-16" : undefined}
        />
      );
      if (config.supports_from_secret) {
        return (
          <WorkspaceSecretRefField
            name={idFieldKey}
            label={api_key_display.label}
            helperText={config.description}
            tooltip={api_key_display.tooltip}
            required={!config.optional}
            disabled={
              isLoading ||
              api_key_display.is_disabled ||
              isDisabled ||
              undefined
            }
            rightOffsetPx={32}
          >
            {apiKeyField}
          </WorkspaceSecretRefField>
        );
      }
      return apiKeyField;
    }
    case "provider_api_toggle":
      const provider_api_toggle_display =
        config.display as ConfigProviderApiToggle;

      return (
        <ProviderApiToggleField
          name={idFieldKey}
          control={form.control}
          config={provider_api_toggle_display}
          defaultValue={config.default}
          isDisabled={isDisabled}
          rules={{ required: !config.optional }}
        />
      );
    case "chips_text":
      const chips_text = config.display as ConfigChipsText;

      return (
        <ChipInputField
          name={idFieldKey}
          label={chips_text.label}
          placeholder={chips_text.placeholder || undefined}
          key={idFieldKey}
          rules={{ required: !config.optional }}
          defaultValue={config.default}
          control={form.control}
          isDisabled={isLoading || isDisabled}
          helperText={config.description || undefined}
          areChipsClosable={chips_text.chips_are_closable}
          errorMessage={config.error}
        />
      );
    case "chips_list_text":
      const chips_list_text = config.display as ConfigChipsListText;

      return (
        <ChipInputListField
          name={idFieldKey}
          label={chips_list_text.label}
          placeholder={chips_list_text.placeholder || undefined}
          key={idFieldKey}
          rules={{ required: !config.optional }}
          defaultValue={config.default}
          control={form.control}
          isOptional={config.optional}
          isDisabled={isLoading || isDisabled}
          helperText={config.description || undefined}
          maxValues={chips_list_text.max_values || undefined}
          areChipsClosable={chips_list_text.chips_are_closable}
          errorMessage={config.error}
        />
      );
    case "config_dynamic_text":
      const config_text_display = config.display as ConfigDynamicText;
      if (
        field_key === "channel" &&
        (node_type === "SlackReaderNode" || node_type === "SlackWriterNode")
      ) {
        return (
          <SlackChannelField
            name={idFieldKey}
            label={config_text_display.label}
            placeholder={config_text_display.placeholder || undefined}
            key={idFieldKey}
            rules={{ required: !config.optional }}
            control={form.control}
            defaultValue={config.default}
            isDisabled={isLoading || isDisabled}
          />
        );
      }

      const dynamicTextHelperContent = config_text_display.help_text ? (
        <span>
          {config.description}{" "}
          <a
            href={config_text_display.help_text}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Read more
            <ExternalLink className="h-3 w-3" />
          </a>
        </span>
      ) : undefined;

      return (
        <NoxusInputField
          name={idFieldKey}
          label={config_text_display.label}
          placeholder={config_text_display.placeholder || undefined}
          helperText={
            dynamicTextHelperContent || config.description || undefined
          }
          tooltip={config_text_display.tooltip || undefined}
          key={idFieldKey}
          rules={{ required: !config.optional }}
          control={form.control}
          defaultValue={config.default}
          disable={isLoading || isDisabled}
          isOptional={config.optional}
          showOptionalLabel={
            config_text_display.show_optional_label ?? config.optional
          }
        />
      );
    case "card_select":
      const card_select_display = config.display as ConfigCardSelect;
      return (
        <CardSelectField
          name={idFieldKey}
          label={card_select_display.label}
          items={card_select_display.items}
          defaultValue={config.default || undefined}
          tooltip={card_select_display.tooltip || undefined}
          isDisabled={
            isLoading || !!card_select_display.is_disabled || isDisabled
          }
          variant={card_select_display.variant ?? "compact"}
          cols={card_select_display.cols}
        />
      );

    case "toggle":
      const toggle_display = config.display as ConfigToggle;
      return (
        <ToggleField
          name={idFieldKey}
          label={toggle_display.label}
          tooltip={toggle_display.tooltip || undefined}
          defaultValue={config.default}
          description={config.description}
          isDisabled={isLoading || !!toggle_display.is_disabled || isDisabled}
          togglePosition={toggle_display.toggle_position ?? "right"}
        />
      );

    case "toggle_group":
      const toggle_group_display =
        config.display as import("@/types/api/spotflow/spotflow-config").ConfigToggleGroup;
      return (
        <ToggleGroupField
          name={idFieldKey}
          display={toggle_group_display}
          isDisabled={isLoading || isDisabled}
        />
      );

    case "toggle_button":
      const toggle_button_display = config.display as ConfigToggleButton;
      return (
        <ToggleButtonField
          label={toggle_button_display.label}
          name={idFieldKey}
          tooltip={toggle_button_display.tooltip || undefined}
          icon={toggle_button_display.icon}
          toggledIcon={toggle_button_display.toggledIcon || undefined}
          isDisabled={
            isLoading || toggle_button_display.is_disabled || isDisabled
          }
          control={form.control}
          rules={{ required: !config.optional }}
        />
      );

    case "config_dict_complex_list":
      const dict_complex_display = config.display as ConfigDictComplexList;
      const dict_complex_node = getNodeById(id);
      const dict_complex_variableConnector =
        dict_complex_node?.data.config.outputs.find(
          (output: IODefinition) => output.type === "variable_connector"
        ) as IODefinition | undefined;

      if (!dict_complex_variableConnector) return <></>;

      return (
        <ComplexDictConfig
          name={idFieldKey}
          label={dict_complex_display.label}
          tooltip={dict_complex_display.tooltip || undefined}
          keys={dict_complex_display.keys}
          subtitle={dict_complex_display.subtitle || undefined}
          control={form.control}
          defaultValue={form.getValues(idFieldKey) || config.default}
          connector={dict_complex_variableConnector}
          nodeId={id}
          addNewDynamicOutput={addNewDynamicOutput}
          removeDynamicOutput={removeDynamicOutput}
          changeDynamicOutputName={changeDynamicOutputName}
          unselectAllNodes={unselectAllNodes}
          options={advancedChipOptions}
          mode="outputs"
          prefix_name={dict_complex_display.prefix_name || "Output"}
          isDisabled={isDisabled}
          isControlled={true}
          collapsible={dict_complex_display.collapsible}
        />
      );

    case "config_dict_complex_list_standalone":
      const dict_complex_inputs_display =
        config.display as ConfigDictComplexListStandalone;
      const dict_complex_inputs_node = getNodeById(id);
      const dict_complex_inputs_variableConnector =
        dict_complex_inputs_node?.data.config.inputs.find(
          (input: IODefinition) =>
            input.type === "variable_connector" ||
            input.type === "variable_type_size_connector"
        ) as IODefinition | undefined;

      if (!dict_complex_inputs_variableConnector) return <></>;

      return (
        <ComplexDictConfig
          name={idFieldKey}
          label={dict_complex_inputs_display.label}
          tooltip={dict_complex_inputs_display.tooltip || undefined}
          keys={dict_complex_inputs_display.keys}
          subtitle={dict_complex_inputs_display.subtitle || undefined}
          control={form.control}
          defaultValue={form.getValues(idFieldKey) || config.default}
          connector={dict_complex_inputs_variableConnector}
          nodeId={id}
          addNewDynamicOutput={addNewDynamicOutput}
          removeDynamicOutput={removeDynamicOutput}
          changeDynamicOutputName={changeDynamicOutputName}
          unselectAllNodes={unselectAllNodes}
          options={advancedChipOptions}
          mode="standalone"
          isDisabled={isDisabled}
        />
      );

    case "config_dict_list_without_connector":
      const config_dict_list_without_connector_display =
        config.display as ConfigDictListWithoutConnector;
      return (
        <DictListField
          name={idFieldKey}
          defaultValue={config.default}
          label={config_dict_list_without_connector_display.label}
          tooltip={
            config_dict_list_without_connector_display.tooltip || undefined
          }
          control={form.control}
          keyLabel={config_dict_list_without_connector_display.key_label}
          valueLabel={config_dict_list_without_connector_display.value_label}
          keyPrefix={config_dict_list_without_connector_display.key_prefix}
          valuePlaceholder={
            config_dict_list_without_connector_display.value_placeholder
          }
          required_keys={
            config_dict_list_without_connector_display.required_keys ||
            undefined
          }
          isDisabled={isDisabled}
        />
      );

    case "config_criteria_list":
      const config_criteria_list_display = config.display as ConfigCriteriaList;
      return (
        <CriteriaListField
          name={idFieldKey}
          defaultValue={config.default}
          label={config_criteria_list_display.label}
          tooltip={config_criteria_list_display.tooltip || undefined}
          control={form.control}
          keyLabel={config_criteria_list_display.key_label}
          valueLabel={config_criteria_list_display.value_label}
          keyPrefix={config_criteria_list_display.key_prefix}
          valuePlaceholder={config_criteria_list_display.value_placeholder}
          isDisabled={isDisabled}
          chipOptions={advancedChipOptions}
          helperText={config.description || undefined}
        />
      );

    case "config_scrape_website_list":
      const scrape_website_list_display =
        config.display as ConfigScrapeWebsiteList;
      return (
        <WebsiteScrapeField
          name={idFieldKey}
          label={scrape_website_list_display.label}
          control={form.control}
          rules={{ required: !config.optional }}
          isDisabled={isDisabled}
          maxDepth={(scrape_website_list_display as any).max_depth ?? 3}
        />
      );

    case "variable_input_value":
      const variable_input_config = config.display as ConfigVariableInputValue;

      if (!variable_type) return <></>;
      // V2 routes fixed values through the shared TypedValueField (same widget
      // as the Run form). V1 keeps its own widgets below, untouched.
      if (isEditorV2) {
        const parsedVarType = parseFlowInputType(variable_type);
        return (
          <TypedValueField
            control={form.control}
            name={idFieldKey}
            label={variable_input_config.label}
            dataType={parsedVarType.dataType}
            isList={parsedVarType.isList}
            isDisabled={isLoading || isDisabled}
            setValue={form.setValue}
            fileVariant="inline"
            disableVariables
          />
        );
      }
      if (variable_type === "str") {
        return (
          <RichTextField
            label={variable_input_config.label}
            control={form.control}
            tooltip={variable_input_config.tooltip || undefined}
            name={idFieldKey}
            config={{
              isExpandable: true,
              hideToolbar: variable_input_config.hide_toolbar || false,
            }}
            isDisabled={isDisabled}
          />
        );
      } else if (variable_type === "number") {
        return (
          <NumberInputField
            control={form.control}
            name={idFieldKey}
            label={variable_input_config.label}
            tooltip={variable_input_config.tooltip || undefined}
            isDisabled={isLoading || isDisabled}
            step="any"
          />
        );
      } else if (variable_type === "bool") {
        return (
          <ToggleField
            name={idFieldKey}
            label={variable_input_config.label}
            tooltip={variable_input_config.tooltip || undefined}
            isDisabled={isLoading || isDisabled}
          />
        );
      } else if (variable_type === "datetime") {
        return (
          <DatePickerField
            name={idFieldKey}
            label={variable_input_config.label}
            tooltip={variable_input_config.tooltip || undefined}
            control={form.control}
            includeTime={true}
            isDisabled={isLoading || isDisabled}
          />
        );
      } else {
        return (
          <FileUploadField
            name={idFieldKey}
            allowed_file_types={fileTypesForDataType(variable_type)}
            variant="logo"
            tooltip={variable_input_config.tooltip || undefined}
            label={variable_input_config.label}
            control={form.control}
            canSelectMultipleFiles={false}
            isDisabled={isLoading || isDisabled}
          />
        );
      }

    case "file_base64":
      // NOTE: this is only used for the vertex provider, which only supports single file upload and its a json
      // we should provide the file types in the config file from backend
      const file_base64_display = config.display as ConfigFileBase64;

      return (
        <FileBase64Field
          name={idFieldKey}
          allowed_file_types={
            (file_base64_display.allowed_file_types as FileTypes[]) || [
              FileTypes.JSON,
            ]
          }
          tooltip={file_base64_display.tooltip || undefined}
          label={file_base64_display.label}
          control={form.control}
          isDisabled={isLoading || isDisabled}
        />
      );

    case "file_array":
      const file_array_display = config.display as ConfigFileArray;

      return (
        <FileUploadField
          name={idFieldKey}
          allowed_file_types={
            (file_array_display.allowed_file_types as FileTypes[]) || [
              FileTypes.CSV,
              FileTypes.TXT,
              FileTypes.PDF,
              FileTypes.JSON,
              FileTypes.MARKDOWN,
              FileTypes.DOCX,
              FileTypes.DOC,
              FileTypes.PPTX,
              FileTypes.PPT,
              FileTypes.JPEG,
              FileTypes.PNG,
              FileTypes.XLSX,
              FileTypes.XLS,
              FileTypes.ZIP,
              FileTypes.XML,
              FileTypes.RTF,
            ]
          }
          // variant={file_array_display.variant || "logo"}
          variant={file_array_display.variant || "logo"}
          tooltip={file_array_display.tooltip || undefined}
          label={file_array_display.label}
          control={form.control}
          isDisabled={isLoading || isDisabled}
          canSelectMultipleFiles={file_array_display.can_select_multiple_files}
          isPublic={file_array_display.isPublic}
        />
      );

    case "image_upload":
      const file_display = config.display as ConfigImageUpload;

      return (
        <div className="flex w-fit justify-start">
          <FileUploadField
            name={idFieldKey}
            allowed_file_types={[FileTypes.IMAGE]}
            variant="image"
            tooltip={file_display?.tooltip || undefined}
            label={file_display?.label || config.display?.label}
            control={form.control}
            isDisabled={isLoading || isDisabled}
            canSelectMultipleFiles={
              file_display?.can_select_multiple_files || false
            }
            isPublic={file_display.isPublic}
          />
        </div>
      );

    case "type_dict_array":
      const type_dict_array_display = config.display as ConfigTypeDictArray;

      const sets_connector_type_and_keys_rules = config.rules
        ?.filter((r) => r.type === "sets_connector_type_and_keys")
        .map((r) => r.connector || "");

      const handleTypeDictArrayChange = (value: ITypeDictValue[]) => {
        if (sets_connector_type_and_keys_rules) {
          updateMultiTypeDictArrayConnectors(
            id,
            sets_connector_type_and_keys_rules,
            value
          );
        }
      };

      return (
        <TypeDictField
          name={idFieldKey}
          values={type_dict_array_display.values}
          type_map={type_dict_array_display.type_map}
          label={type_dict_array_display.label}
          subtitle={(type_dict_array_display as any).subtitle || undefined}
          control={form.control}
          tooltip={type_dict_array_display.tooltip || undefined}
          defaultValue={config.default}
          isDisabled={isLoading || isDisabled}
          onChange={handleTypeDictArrayChange}
          default_prefix={type_dict_array_display.default_prefix}
          default_value={type_dict_array_display.default_value}
          default_type={type_dict_array_display.default_type}
          setsConnectorKeys={!!sets_connector_type_and_keys_rules}
          min_items={(type_dict_array_display as any).min_items ?? 1}
        />
      );
    case "dict_select_array": {
      const dict_select_array_display = config.display as ConfigDictSelectArray;
      const dsa_any = dict_select_array_display as any;
      const dsa_name_values =
        (dsa_any.name_values as string[] | null | undefined) ?? undefined;
      const dsa_unique_values =
        (dsa_any.unique_values as boolean | undefined) ?? false;
      const dsa_bound_shape_field =
        (dsa_any.bound_shape_field as string | null | undefined) ?? undefined;
      const dsaProps = {
        name: idFieldKey,
        label: dict_select_array_display.label,
        control: form.control,
        tooltip: dict_select_array_display.tooltip || undefined,
        helperText: config.description || undefined,
        defaultValue: config.default,
        isDisabled: isLoading || isDisabled,
        name_label: dict_select_array_display.name_label,
        name_prefix: dict_select_array_display.name_prefix,
        value_label: dict_select_array_display.value_label,
        values: dict_select_array_display.values,
        default_value: dict_select_array_display.default_value,
        name_values: dsa_name_values,
        unique_values: dsa_unique_values,
        min_items: (dsa_any.min_items as number | undefined) ?? 1,
        addLabel: (dsa_any.add_label as string | undefined) || undefined,
      };
      if (dsa_bound_shape_field && id) {
        return (
          <DictSelectArrayWithBoundShape
            {...dsaProps}
            nodeId={id}
            suggestionsField={dsa_bound_shape_field}
          />
        );
      }
      return <SelectArrayDictField {...dsaProps} />;
    }
    case "input_variable_selector":
      const input_variable_selector_display =
        config.display as ConfigInputVariableSelector;

      if (isEditorV2) {
        return (
          <InputVariableSelectorField
            name={idFieldKey}
            label={input_variable_selector_display.label}
            control={form.control}
            tooltip={input_variable_selector_display.tooltip || undefined}
            helperText={config.description || undefined}
            defaultValue={config.default}
            isDisabled={isLoading || isDisabled}
            name_label={input_variable_selector_display.name_label}
            name_prefix={input_variable_selector_display.name_prefix}
            value_label={input_variable_selector_display.value_label}
            default_value={input_variable_selector_display.default_value}
            min_items={(input_variable_selector_display as any).min_items ?? 1}
            addLabel={
              (input_variable_selector_display as any).add_label || undefined
            }
            chipOptions={advancedChipOptions}
          />
        );
      }

      return (
        <SelectArrayDictField
          name={idFieldKey}
          label={input_variable_selector_display.label}
          control={form.control}
          tooltip={input_variable_selector_display.tooltip || undefined}
          helperText={config.description || undefined}
          defaultValue={config.default}
          isDisabled={isLoading || isDisabled}
          name_label={input_variable_selector_display.name_label}
          name_prefix={input_variable_selector_display.name_prefix}
          value_label={input_variable_selector_display.value_label}
          values={input_variable_selector_display.values}
          default_value={input_variable_selector_display.default_value}
          min_items={(input_variable_selector_display as any).min_items ?? 1}
          addLabel={
            (input_variable_selector_display as any).add_label || undefined
          }
        />
      );
    case "dict_text_and_select_array":
      const dict_text_and_select_array_display =
        config.display as ConfigDictTextAndSelectArray;

      return (
        <DictTextAndSelectArrayField
          name={idFieldKey}
          label={dict_text_and_select_array_display.label}
          control={form.control}
          tooltip={dict_text_and_select_array_display.tooltip || undefined}
          defaultValue={config.default}
          isDisabled={isLoading || isDisabled}
          name_labels={dict_text_and_select_array_display.name_labels}
          name_prefixes={dict_text_and_select_array_display.name_prefixes}
          value_label={dict_text_and_select_array_display.select_label}
          values={dict_text_and_select_array_display.select_values}
          default_value={
            dict_text_and_select_array_display.select_default_value
          }
        />
      );
    case "time_picker":
      const time_picker_display = config.display as ConfigTimePicker;

      return (
        <NoxusInputField
          name={idFieldKey}
          label={time_picker_display.label}
          tooltip={time_picker_display.tooltip || undefined}
          showUtcTimeHint={time_picker_display.utc}
          helperText={config.description || undefined}
          errors={form.formState.errors}
          control={form.control}
          type="time"
          disable={isDisabled}
        />
      );

    case "array_field":
      const array_field_display = config.display as ConfigArrayField;

      return (
        <ArrayFieldWrapper
          name={idFieldKey}
          label={array_field_display.label}
          addLabel={array_field_display.add_label || "Add"}
          fieldType={
            (array_field_display.field_type as
              | "time"
              | "text"
              | "number"
              | undefined) || "text"
          }
          summaryTemplate={array_field_display.summary_template}
          showUtcTimeHint={array_field_display.utc}
          tooltip={array_field_display.tooltip || undefined}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default as string[] | undefined}
        />
      );

    case "multi_select":
      const multi_select_display = config.display as ConfigMultiSelect;

      return (
        <MultiSelectField
          name={idFieldKey}
          label={multi_select_display.label}
          tooltip={multi_select_display.tooltip || undefined}
          control={form.control}
          helperText={config.description || undefined}
          options={multi_select_display.values as OptionType[]}
          placeholder={multi_select_display.placeholder}
          minOptions={multi_select_display.min_options ?? undefined}
          selectAll={multi_select_display.select_all ?? false}
          isDisabled={
            isLoading || !!multi_select_display.is_disabled || isDisabled
          }
        />
      );

    case "output_selector":
      const output_selector_display = config.display as ConfigOutputSelector;
      const output_selector_node = getNodeById(id);
      const output_variableConnector =
        (output_selector_node?.data.config.outputs.find(
          (output: IODefinition) =>
            output.type === "variable_connector" ||
            output.type === "variable_type_size_connector"
        ) as IODefinition | undefined) ??
        // V2 native nodes have no variable connector — the form fields ARE the
        // node's (dynamic) outputs. Synthesize a connector from those output
        // names so the form-field builder still renders. V1 nodes always have
        // a real variable connector, so this fallback never triggers for them.
        (output_selector_node
          ? ({
              type: "variable_type_size_connector",
              name: output_selector_display.connector_name,
              keys: (output_selector_node.data.config.outputs ?? []).map(
                (o: IODefinition) => o.name
              ),
            } as unknown as IODefinition)
          : undefined);

      if (!output_variableConnector)
        return (
          <Banner
            label={
              "There is some misconfiguration with this configuration. If the problem persists, please contact support."
            }
            style="error"
            size="sm"
          />
        );
      return (
        <OutputSelectorField
          name={idFieldKey}
          control={form.control}
          defaultValue={config.default}
          label={output_selector_display.label}
          connector={output_variableConnector}
          nodeId={id}
          chipOptions={advancedChipOptions}
          isDisabled={isDisabled}
        />
      );

    case "user_selector":
      const user_selector_display = config.display as ConfigUserSelector;
      return (
        <UserSelectField
          name={idFieldKey}
          label={user_selector_display.label}
          tooltip={user_selector_display.tooltip || undefined}
          placeholder={user_selector_display.placeholder || undefined}
          control={form.control}
          defaultValue={config.default}
          isDisabled={isDisabled}
          isOptional={config.optional}
        />
      );

    case "color_select":
      const color_select_display = config.display as ConfigColorSelect;
      return (
        <ColorSelectField
          name={idFieldKey}
          control={form.control}
          label={color_select_display.label}
          isDisabled={isDisabled}
          defaultValue={config.default}
          hasCustomColor={color_select_display.has_custom_color ?? false}
          isBig={color_select_display.is_big}
          options={color_select_display.values.map((value) => ({
            value: value.value,
            color: value.color,
          }))}
        />
      );

    case "theme_select":
      const theme_select_display =
        config.display as ConfigColorThemeSelectField;
      return (
        <ColorThemeSelectField
          name={idFieldKey}
          control={form.control}
          label={theme_select_display.label}
          isDisabled={isDisabled}
          defaultValue={config.default}
          hasCustomColor={theme_select_display.has_custom_color ?? false}
          isBig={theme_select_display.is_big}
        />
      );

    case "user_table_selector":
      const user_table_selector_display =
        config.display as ConfigUserTableSelector;
      return (
        <UsersTableField
          name={idFieldKey}
          // label={user_selector_display.label}
          // tooltip={user_selector_display.tooltip || undefined}
          // placeholder={user_selector_display.placeholder || undefined}
          control={form.control}
          defaultValue={config.default}
          isDisabled={isDisabled}
        />
      );

    case "mcp_credential_select":
      const mcp_credential_select_display =
        config.display as ConfigMCPCredentialSelect;
      return (
        <MCPCredentialField
          name={idFieldKey}
          control={form.control}
          label={mcp_credential_select_display.label}
          tooltip={mcp_credential_select_display.tooltip}
          isDisabled={isDisabled}
        />
      );

    case "mcp_credential_picker": {
      const mcp_credential_picker_display =
        config.display as ConfigMCPCredentialPicker;
      return (
        <MCPCredentialPickerField
          name={idFieldKey}
          control={form.control}
          label={mcp_credential_picker_display.label}
          tooltip={mcp_credential_picker_display.tooltip}
          placeholder={mcp_credential_picker_display.placeholder}
          isDisabled={isDisabled}
        />
      );
    }

    case "mcp_tool_picker": {
      const mcp_tool_picker_display = config.display as ConfigMCPToolPicker;
      // ``credential_field`` is the sibling config key that holds the picked
      // credential UUID. In the node's config form the key is prefixed with
      // the same parent path (e.g. ``node.{nodeId}.config.credential_id``) —
      // derive it from this field's key by replacing the trailing segment.
      const credentialFieldName = mcp_tool_picker_display.credential_field
        ? idFieldKey.replace(/[^.]+$/, mcp_tool_picker_display.credential_field)
        : idFieldKey.replace(/[^.]+$/, "credential_id");
      return (
        <MCPToolPickerField
          name={idFieldKey}
          control={form.control}
          label={mcp_tool_picker_display.label}
          tooltip={mcp_tool_picker_display.tooltip}
          placeholder={mcp_tool_picker_display.placeholder}
          credentialField={credentialFieldName}
          isDisabled={isDisabled}
        />
      );
    }

    case "tools_select":
      const tools_select_display = config.display as ConfigToolsSelect;
      return (
        <ConfigToolsField
          name={idFieldKey}
          control={form.control}
          quickTools={tools_select_display.quick_tools || undefined}
          isNodeConfig={true}
          showSections={tools_select_display.show_sections || undefined}
          isDisabled={isDisabled}
          defaultValue={config.default}
          itemConfigByType={config.item_config_by_type}
        />
      );

    case "languages_list": {
      const languages_display = config.display as ConfigLanguagesList;
      // `default_language` is a sibling field on the same settings object, so
      // the picker writes both — the list alone cannot express which is default.
      const defaultLanguageKey = idFieldKey.replace(
        /languages$/,
        "default_language"
      );
      return (
        <Controller
          control={form.control}
          name={idFieldKey}
          defaultValue={config.default}
          render={({ field }) => (
            <LanguagesField
              languages={field.value as AgentLanguageInput[] | undefined}
              onLanguagesChange={field.onChange}
              defaultLanguage={form.watch(defaultLanguageKey) as string | null}
              onDefaultLanguageChange={(code) =>
                form.setValue(defaultLanguageKey, code, { shouldDirty: true })
              }
              isDisabled={isDisabled}
              introPlaceholder={languages_display.intro_placeholder}
              options={languages_display.options ?? []}
              addableCodes={languages_display.addable_codes ?? []}
            />
          )}
        />
      );
    }

    case "intro_languages_warning": {
      const warning_display = config.display as ConfigIntroLanguagesWarning;
      // The deployment's language select is a sibling field: the warning
      // checks the agent's greetings against whatever it holds.
      const selectedLanguage = form.watch(
        idFieldKey.replace(
          /[^.]+$/,
          warning_display.language_field ?? "language"
        )
      ) as string | undefined;
      return (
        <IntroLanguagesWarning
          language={selectedLanguage}
          locales={warning_display.locales ?? []}
          agentLanguages={warning_display.agent_languages ?? []}
          agentDefaultLanguage={warning_display.agent_default_language ?? null}
          agentEditable={warning_display.agent_editable ?? true}
          assistantId={warning_display.assistant_id ?? null}
          isDisabled={isDisabled}
        />
      );
    }

    case "skills_list":
      return (
        <SkillsFieldWithHeader
          form={form}
          idFieldKey={idFieldKey}
          config={config}
          isDisabled={isDisabled}
        />
      );

    case "guidelines_list": {
      return (
        <GuidelinesFieldWithHeader
          form={form}
          idFieldKey={idFieldKey}
          config={config}
          isDisabled={isDisabled}
        />
      );
    }

    case "glossary_list":
      return (
        <GlossaryFieldStandalone
          form={form}
          idFieldKey={idFieldKey}
          config={config}
          isDisabled={isDisabled}
        />
      );
    case "agent_memory_field":
      return (
        <AgentMemoryFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
        />
      );

    case "base_tools_field":
      return (
        <BaseToolsFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
          itemConfig={config.item_config}
          itemConfigByType={config.item_config_by_type}
        />
      );

    case "knowledge_bases_field":
      return (
        <KnowledgeBasesFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
          itemConfig={config.item_config}
        />
      );

    case "files_field":
      return (
        <FilesFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
        />
      );

    case "databases_field":
      return (
        <DatabasesFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
        />
      );

    case "tables_field":
      return (
        <TablesFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
        />
      );

    case "variables_field":
      return (
        <ExternalValuesField
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          label={config.display?.label || undefined}
        />
      );

    case "workflows_field":
      return (
        <WorkflowsFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
          itemConfig={config.item_config}
        />
      );

    case "coworkers_field":
      return (
        <CoworkersFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
          itemConfig={config.item_config}
        />
      );

    case "actions_field":
      return (
        <ActionsFieldNCL
          name={idFieldKey}
          control={form.control}
          isDisabled={isDisabled}
          defaultValue={config.default}
        />
      );

    case "enum_slider":
      const enum_slider_display = config.display as ConfigEnumSlider;
      return (
        <EnumSliderField
          name={idFieldKey}
          label={enum_slider_display.label}
          tooltip={enum_slider_display.tooltip || undefined}
          control={form.control}
          values={enum_slider_display.values}
          defaultValue={config.default}
          isDisabled={
            isLoading || enum_slider_display.is_disabled || isDisabled
          }
          isHorizontal={enum_slider_display.is_horizontal}
        />
      );

    case "guardrail_card": {
      const guardrail_display = config.display as ConfigGuardrailCard;
      return (
        <GuardrailCard
          control={form.control}
          name={idFieldKey}
          label={guardrail_display.label}
          values={guardrail_display.values}
          offValue={guardrail_display.off_value}
          contextPlaceholder={
            guardrail_display.context_placeholder || undefined
          }
          accordionLabel={guardrail_display.accordion_label}
          isDisabled={isLoading || guardrail_display.is_disabled || isDisabled}
        />
      );
    }

    case "coworker_select":
      const coworker_select_display = config.display as ConfigCoworkerSelect;
      return (
        <CoworkerSelectField
          name={`${id}.${field_key}`}
          label={coworker_select_display.label}
          tooltip={coworker_select_display.tooltip || undefined}
          control={form.control}
          defaultValue={config.default}
          isDisabled={
            isLoading || coworker_select_display.is_disabled || isDisabled
          }
          includeCurrentNode={coworker_select_display.include_current_node}
        />
      );

    case "json_schema_builder":
      const json_schema_builder_display =
        config.display as ConfigJsonSchemaBuilder;
      return (
        <JsonSchemaBuilderConfig
          name={idFieldKey}
          label={json_schema_builder_display.label}
          subtitle={json_schema_builder_display.subtitle || undefined}
          tooltip={json_schema_builder_display.tooltip || undefined}
          control={form.control}
          defaultValue={config.default}
          keys={json_schema_builder_display.keys}
          prefix_name={json_schema_builder_display.prefix_name || "Field"}
          isDisabled={isDisabled}
          allow_dots_in_name={json_schema_builder_display.allow_dots_in_name}
          allow_spaces_in_name={
            json_schema_builder_display.allow_spaces_in_name
          }
          min_items={(json_schema_builder_display as any).min_items ?? 1}
        />
      );

    case "extraction_schema_builder":
      const extraction_schema_display =
        config.display as ConfigExtractionSchema;
      return (
        <ExtractionSchemaBuilderConfig
          name={idFieldKey}
          label={extraction_schema_display.label}
          subtitle={extraction_schema_display.subtitle || undefined}
          tooltip={extraction_schema_display.tooltip || undefined}
          control={form.control}
          defaultValue={config.default}
          typeOptions={extraction_schema_display.type_options}
          availableFields={extraction_schema_display.available_fields}
          forApproval={extraction_schema_display.for_approval}
          allowLinking={extraction_schema_display.allow_linking}
          inheritedLayout={extraction_schema_display.case_layout ?? null}
          isDisabled={isDisabled}
          // Form paths are node-id-prefixed like every other config key.
          layoutName={
            extraction_schema_display.layout_key
              ? id
                ? `${id}.${extraction_schema_display.layout_key}`
                : extraction_schema_display.layout_key
              : undefined
          }
        />
      );

    case "playbook_editor":
      const {
        PlaybookEditor,
      } = require("@/components/BrowserAutomation/PlaybookEditor");
      return (
        <PlaybookEditor
          name={idFieldKey}
          form={form}
          label={config.display.label || "Playbook"}
          show_recorder_button={
            (config.display as any).show_recorder_button ?? true
          }
          show_test_button={(config.display as any).show_test_button ?? true}
        />
      );

    case "icon_picker":
      const { IconPickerField } = require("@/components/Form/IconPickerField");
      const icon_picker_display = config.display as ConfigIconPicker;
      return (
        <IconPickerField
          name={idFieldKey}
          control={form.control}
          label={icon_picker_display.label || "Icon"}
          isDisabled={isDisabled}
          isLarge={icon_picker_display.is_large}
          side={icon_picker_display.side}
          align={icon_picker_display.align}
        />
      );

    case "config_bindable_text": {
      const bindable_text_display = config.display as ConfigBindableText;
      if (!id) return defaultFieldFallback;
      return (
        <BindableTextField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_text_display.label ?? field_key}
          tooltip={bindable_text_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          placeholder={bindable_text_display.placeholder ?? undefined}
          defaultToVariable={config.default_to_variable}
          dataTypeField={bindable_text_display.data_type_field ?? undefined}
          dataType={bindable_text_display.data_type ?? undefined}
          variableOnly={bindable_text_display.variable_only ?? undefined}
          isList={bindable_text_display.is_list ?? false}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_text_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_big_text": {
      const bindable_big_text_display = config.display as ConfigBindableBigText;
      if (!id) return defaultFieldFallback;
      return (
        <BindableBigTextField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_big_text_display.label ?? field_key}
          tooltip={bindable_big_text_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          placeholder={bindable_big_text_display.placeholder ?? undefined}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_big_text_display.show_optional_label ?? config.optional
          }
          enableHeuristics={
            bindable_big_text_display.enable_heuristics ?? undefined
          }
          heuristicContext={
            bindable_big_text_display.heuristic_context ?? undefined
          }
          heuristicTypes={
            bindable_big_text_display.heuristic_types ?? undefined
          }
          improverContext={
            bindable_big_text_display.improver_context ?? undefined
          }
          improverPrompt={
            bindable_big_text_display.improver_prompt ?? undefined
          }
        />
      );
    }
    case "config_bindable_code": {
      const bindable_code_display = config.display as ConfigBindableCode;
      if (!id) return defaultFieldFallback;
      return (
        <BindableCodeOrBigTextField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_code_display.label ?? field_key}
          tooltip={bindable_code_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          placeholder={bindable_code_display.placeholder ?? undefined}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_code_display.show_optional_label ?? config.optional
          }
          language={bindable_code_display.language ?? undefined}
          languageField={bindable_code_display.language_field ?? undefined}
          aiGenerate={bindable_code_display.ai_generate ?? undefined}
          numberOfLines={bindable_code_display.number_of_lines ?? 5}
          markdownModeField={
            bindable_code_display.markdown_mode_field ?? undefined
          }
        />
      );
    }
    case "config_bindable_number": {
      const bindable_number_display = config.display as ConfigBindableNumber;
      if (!id) return defaultFieldFallback;
      return (
        <BindableNumberField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_number_display.label ?? field_key}
          clearsToEmpty={bindable_number_display.clears_to_empty}
          allowNegative={bindable_number_display.allow_negative}
          integerOnly={bindable_number_display.integer}
          tooltip={bindable_number_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_number_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_datetime": {
      const bindable_datetime_display =
        config.display as ConfigBindableDateTime;
      if (!id) return defaultFieldFallback;
      return (
        <BindableDateTimeField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_datetime_display.label ?? field_key}
          tooltip={bindable_datetime_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_datetime_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_bool": {
      const bindable_bool_display = config.display as ConfigBindableBool;
      if (!id) return defaultFieldFallback;
      return (
        <BindableBoolField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_bool_display.label ?? field_key}
          tooltip={bindable_bool_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_bool_display.show_optional_label ?? config.optional
          }
          defaultValue={
            typeof config.default === "boolean" ? config.default : undefined
          }
          widget={bindable_bool_display.widget ?? "switch"}
        />
      );
    }
    case "config_bindable_select": {
      const bindable_select_display = config.display as ConfigBindableSelect;
      if (!id) return defaultFieldFallback;
      return (
        <BindableSelectField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_select_display.label ?? field_key}
          tooltip={bindable_select_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          placeholder={bindable_select_display.placeholder ?? undefined}
          values={bindable_select_display.values ?? []}
          disabled={isDisabled}
          isRequired={!config.optional}
        />
      );
    }
    case "config_bindable_chips_select": {
      const bindable_chips_display =
        config.display as ConfigBindableChipsSelect;
      if (!id) return defaultFieldFallback;
      return (
        <BindableChipsSelectField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_chips_display.label ?? field_key}
          tooltip={bindable_chips_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          values={bindable_chips_display.values ?? []}
          isMulti={bindable_chips_display.is_multi ?? false}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_chips_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_file": {
      const bindable_file_display = config.display as ConfigBindableFile;
      if (!id) return defaultFieldFallback;
      return (
        <BindableFileField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_file_display.label ?? field_key}
          tooltip={bindable_file_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isList={bindable_file_display.is_list}
          allowedFileTypes={
            (bindable_file_display.allowed_file_types as FileTypes[] | null) ??
            undefined
          }
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_file_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_dict_rows": {
      const bindable_dict_rows_display =
        config.display as ConfigBindableDictRows;
      if (!id) return defaultFieldFallback;
      return (
        <BindableDictRowsField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_dict_rows_display.label ?? field_key}
          tooltip={bindable_dict_rows_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_dict_rows_display.show_optional_label ?? config.optional
          }
          lockedRows={
            bindable_dict_rows_display.locked_rows
              ? (config.row_keys ?? [])
              : undefined
          }
          emptyMessage={bindable_dict_rows_display.empty_message ?? undefined}
        />
      );
    }
    case "config_bindable_typed_dict_rows": {
      const bindable_typed_dict_rows_display =
        config.display as ConfigBindableTypedDictRows;
      if (!id) return defaultFieldFallback;
      return (
        <BindableTypedDictRowsField
          nodeId={id}
          fieldKey={field_key}
          values={bindable_typed_dict_rows_display.values}
          defaultValue={bindable_typed_dict_rows_display.default_value}
          addLabel={bindable_typed_dict_rows_display.add_label ?? undefined}
          label={bindable_typed_dict_rows_display.label ?? field_key}
          tooltip={bindable_typed_dict_rows_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_typed_dict_rows_display.show_optional_label ??
            config.optional
          }
        />
      );
    }
    case "config_bindable_dict_select_rows": {
      const bindable_dict_select_rows_display =
        config.display as ConfigBindableDictSelectRows;
      if (!id) return defaultFieldFallback;
      return (
        <BindableDictSelectRowsField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_dict_select_rows_display.label ?? field_key}
          values={bindable_dict_select_rows_display.values}
          placeholder={
            bindable_dict_select_rows_display.placeholder ?? undefined
          }
          addLabel={bindable_dict_select_rows_display.add_label ?? undefined}
          dataType={bindable_dict_select_rows_display.data_type ?? "str"}
          description={config.description ?? undefined}
          disabled={isDisabled}
        />
      );
    }
    case "config_structured_fields": {
      const structured_fields_display =
        config.display as ConfigStructuredFields;
      if (!id) return defaultFieldFallback;
      return (
        <StructuredFieldsField
          nodeId={id}
          fieldKey={field_key}
          label={structured_fields_display.label ?? field_key}
          tooltip={structured_fields_display.tooltip ?? undefined}
          subtitle={structured_fields_display.subtitle ?? undefined}
          description={config.description ?? undefined}
          typeOptions={structured_fields_display.type_options ?? []}
          disabled={isDisabled}
        />
      );
    }
    case "config_table_filter_rows": {
      const table_filter_rows_display = config.display as ConfigTableFilterRows;
      if (!id) return defaultFieldFallback;
      return (
        <TableFilterRowsField
          nodeId={id}
          fieldKey={field_key}
          label={table_filter_rows_display.label ?? field_key}
          columns={table_filter_rows_display.columns}
          addLabel={table_filter_rows_display.add_label ?? undefined}
          emptyMessage={table_filter_rows_display.empty_message ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
        />
      );
    }
    case "config_bindable_condition": {
      const bindable_condition_display =
        config.display as ConfigBindableCondition;
      if (!id) return defaultFieldFallback;
      return (
        <BindableConditionField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_condition_display.label ?? field_key}
          tooltip={bindable_condition_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_condition_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_routes": {
      const bindable_routes_display = config.display as ConfigBindableRoutes;
      if (!id) return defaultFieldFallback;
      return (
        <BindableRoutesField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_routes_display.label ?? field_key}
          tooltip={bindable_routes_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_routes_display.show_optional_label ?? config.optional
          }
        />
      );
    }
    case "config_bindable_switch": {
      const bindable_switch_display = config.display as ConfigBindableSwitch;
      if (!id) return defaultFieldFallback;
      return (
        <SwitchBranchesField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_switch_display.label ?? field_key}
          tooltip={bindable_switch_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
        />
      );
    }
    case "config_path_values": {
      const path_values_display = config.display as ConfigPathValues;
      if (!id) return defaultFieldFallback;
      return (
        <PathValuesField
          nodeId={id}
          fieldKey={field_key}
          label={path_values_display.label ?? field_key}
          tooltip={path_values_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
        />
      );
    }

    case "config_bindable_merge_paths": {
      const bindable_merge_paths_display =
        config.display as ConfigBindableMergePaths;
      if (!id) return defaultFieldFallback;
      return (
        <BindableMergePathsField
          nodeId={id}
          fieldKey={field_key}
          label={bindable_merge_paths_display.label ?? field_key}
          tooltip={bindable_merge_paths_display.tooltip ?? undefined}
          description={config.description ?? undefined}
          disabled={isDisabled}
          isRequired={!config.optional}
          showOptionalLabel={
            bindable_merge_paths_display.show_optional_label ?? config.optional
          }
        />
      );
    }

    default:
      return defaultFieldFallback;
  }
};

const getLabel = (key: string, nodeType: string) => {
  if (nodeType === "ConstantStringNode") {
    if (key === "value") {
      return "Text";
    }
  }
  if (nodeType === "DriveReaderNode") {
    if (key === "file_path") {
      return "Path to file";
    }
  }
  if (nodeType === "DriveFolderDownloaderNode") {
    if (key === "folder_id") {
      return "Folder id";
    }
  }
  if (nodeType === "GmailSenderNode") {
    if (key === "draft") {
      return "Draft (true/false)";
    }
  }
  if (nodeType === "GSpreadsheetReaderNode") {
    if (key === "spreadsheet") {
      return "Spreadsheet ID";
    }
    if (key === "range") {
      return "Cell range";
    }
  }
  if (nodeType === "WebResearchNode") {
    if (key === "prompt") {
      return "Query";
    }
  }
  /* Don't think this is in use */
  if (nodeType === "OpenAINode") {
    if (key === "prompt") {
      return "Query";
    }
    if (key === "T") {
      return "Temperature";
    }
    if (key === "depth") {
      return "Depth";
    }
  }

  return key;
};
